import { Subscription } from 'rxjs';
import { AgentDto } from '../../services/agent.service';
import { AgentProfilePictureChange, AgentProfilePictureService } from '../../services/agent-profile-picture.service';

/**
 * Profile picture links for the agents shown on a page. List loads can finish in any order and
 * the editor can save changes at any time, so each agent remembers the newest information about
 * it (a load's sequence number, or a local change) and older results are ignored.
 */
export class AgentPictureUrls {
  /** Link by agent id, for agents that have a photo. */
  urls: Record<string, string> = {};

  private loadSeq = 0;
  /** The newest load or local change per agent; a result only applies if it is still the newest. */
  private latest = new Map<string, number>();
  private changes: Subscription;

  constructor(private pictureService: AgentProfilePictureService) {
    // The service emits every saved change, even one whose editor has since been closed.
    this.changes = pictureService.changes$.subscribe(change => this.apply(change));
  }

  /** Call when an agent-list request starts; pass the result to {@link load} with its response. */
  begin(): number {
    return ++this.loadSeq;
  }

  /** Applies a list response, for agents with nothing newer than the request that produced it. */
  load(agents: AgentDto[], seq: number): void {
    const current = agents.filter(a => (this.latest.get(a.agentId) ?? 0) < seq);
    current.forEach(a => this.latest.set(a.agentId, seq));

    // Agents this list says have no photo (e.g. another admin removed it).
    const removed = current.filter(a => !a.profilePictureUpdatedAt).map(a => a.agentId);
    this.update(next => removed.forEach(id => delete next[id]));

    const ids = current.filter(a => a.profilePictureUpdatedAt).map(a => a.agentId);
    if (ids.length === 0) {
      return;
    }
    this.pictureService.getMany(ids).subscribe({
      next: pictures => {
        const found = new Map(pictures.filter(p => p.url).map(p => [p.agentId, p.url as string]));
        this.update(next =>
          ids
            .filter(id => this.latest.get(id) === seq)
            .forEach(id => {
              const url = found.get(id);
              if (url) {
                next[id] = url;
              } else {
                delete next[id];
              }
            })
        );
      },
      error: error => console.error('Error loading profile pictures:', error)
    });
  }

  dispose(): void {
    this.changes.unsubscribe();
  }

  private apply({ agentId, picture }: AgentProfilePictureChange): void {
    // Newer than any load in flight, so those loads can no longer overwrite it.
    this.latest.set(agentId, ++this.loadSeq);
    this.update(next => {
      if (picture?.url) {
        next[agentId] = picture.url;
      } else {
        delete next[agentId];
      }
    });
  }

  private update(change: (next: Record<string, string>) => void): void {
    const next = { ...this.urls };
    change(next);
    this.urls = next;
  }
}
